<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import { db } from "../db/index.js";
    import FlashcardModeSelector from "@edu-quizz/shared/components/flashcard/FlashcardModeSelector.svelte";
    import BackButton from "@edu-quizz/shared/components/common/BackButton.svelte";

    export let params = {};

    let deck = null;
    let loading = true;
    let error = null;

    async function getLastReview(cards) {
        let lastReview = null;
        let lastReviewDate = null;

        for (const card of cards) {
            const reviews = await db.flashcardReviews
                .where("flashcardCardServerId")
                .equals(card.serverId)
                .sortBy('reviewedAt');

            if (reviews.length > 0) {
                const cardLastReview = reviews[reviews.length - 1];
                const reviewDate = new Date(cardLastReview.reviewedAt);

                if (!lastReviewDate || reviewDate > lastReviewDate) {
                    lastReview = cardLastReview;
                    lastReviewDate = reviewDate;
                }
            }
        }

        return lastReview;
    }

    async function loadDeck() {
        loading = true;
        error = null;

        try {
            const deckId = parseInt(params.id);

            // Get the flashcard deck with its cards from the database
            const deckData = await db.getFlashcardDeckWithCards(deckId);

            if (!deckData) {
                error = "Deck non trouvé";
                loading = false;
                return;
            }

            deck = deckData;

            // Get the last review for this deck
            const lastReview = await getLastReview(deckData.cards || []);

            // If has reviews, redirect to the mode used in the last review
            if (lastReview) {
                const mode = lastReview.playMode || 'spaced'; // Default to spaced if playMode is missing
                push(`/flashcard/${params.id}/${mode}`);
                return;
            }

            // No reviews, show mode selection screen
            loading = false;
        } catch (err) {
            console.error("Error loading deck:", err);
            error = err.message;
            loading = false;
        }
    }

    function handleModeSelection(mode) {
        push(`/flashcard/${params.id}/${mode}`);
    }

    function goBack() {
        push("/");
    }

    onMount(() => {
        loadDeck();
    });
</script>

<div class="mode-selection-page">
    <header class="page-header">
        <BackButton onClick={goBack} />
        {#if deck}
            <h1 class="text-base-content">{deck.name}</h1>
        {/if}
    </header>

    {#if loading}
        <div class="loading">
            <div class="spinner loading-spinner loading-lg border-primary border-t-transparent"></div>
            <p>Chargement du deck...</p>
        </div>
    {:else if error}
        <div class="error text-base-content/70">
            <p>Erreur: {error}</p>
            <button class="btn btn-primary" on:click={loadDeck}>Réessayer</button>
        </div>
    {:else}
        <div class="mode-selector-container">
            {#if deck.description}
                <p class="deck-description bg-base-200">{deck.description}</p>
            {/if}
            <FlashcardModeSelector onSelectMode={handleModeSelection} />
        </div>
    {/if}
</div>

<style>
    .mode-selection-page {
        min-height: 100vh;
        padding: 1.5rem;
        max-width: 800px;
        margin: 0 auto;
    }

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    h1 {
        margin: 0;
        font-size: 2rem;
    }

    .loading,
    .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 1rem;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .error button {
        border: none;
        padding: 0.6rem 1.2rem;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.95rem;
    }

    .mode-selector-container {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    .deck-description {
        font-size: 1rem;
        line-height: 1.6;
        margin: 0;
        padding: 1rem;
        border-radius: 8px;
    }

    @media (max-width: 640px) {
        .mode-selection-page {
            padding: 1rem;
        }

        h1 {
            font-size: 1.5rem;
        }
    }
</style>
